import { useState } from 'react';
import { Trash2, Plus } from 'lucide-react';
import { adminProblemsApi, type AdminTestCase, type UpsertTestCaseRequest } from '@/lib/api/admin-problems';
import { toast } from 'sonner';
import { cn } from '@/lib/cn';
import { Button } from '@/components/ui/button';
import { PRIMARY_ACTION } from './actionStyles';

interface Props {
  problemId: string;
  testCases: AdminTestCase[];
  onRefresh: () => void;
}

export function ProblemTestCasesTab({ problemId, testCases, onRefresh }: Props) {
  const [saving, setSaving] = useState<string | null>(null);
  const [edits, setEdits] = useState<Record<string, Partial<UpsertTestCaseRequest>>>({});

  function getEdit(tc: AdminTestCase): UpsertTestCaseRequest {
    return {
      input:     edits[tc.id]?.input    ?? tc.input,
      output:    edits[tc.id]?.output   ?? tc.output,
      is_sample: edits[tc.id]?.is_sample ?? tc.is_sample,
      order:     tc.order,
    };
  }

  function setField(tcId: string, field: keyof UpsertTestCaseRequest, value: string | boolean | number) {
    setEdits(prev => ({ ...prev, [tcId]: { ...prev[tcId], [field]: value } }));
  }

  async function saveRow(tc: AdminTestCase) {
    try {
      setSaving(tc.id);
      await adminProblemsApi.updateTestCase(problemId, tc.id, getEdit(tc));
      setEdits(prev => { const next = { ...prev }; delete next[tc.id]; return next; });
      toast.success('Test case saved');
      onRefresh();
    } catch {
      toast.error('Failed to save test case');
    } finally {
      setSaving(null);
    }
  }

  async function deleteRow(tcId: string) {
    try {
      setSaving(tcId);
      await adminProblemsApi.deleteTestCase(problemId, tcId);
      toast.success('Test case deleted');
      onRefresh();
    } catch {
      toast.error('Failed to delete test case');
    } finally {
      setSaving(null);
    }
  }

  async function addRow() {
    try {
      setSaving('new');
      await adminProblemsApi.addTestCase(problemId, {
        input: '',
        output: '',
        is_sample: false,
        order: testCases.length,
      });
      toast.success('Test case added');
      onRefresh();
    } catch {
      toast.error('Failed to add test case');
    } finally {
      setSaving(null);
    }
  }

  const sampleCount = testCases.filter(tc => tc.is_sample).length;

  return (
    <div className="flex flex-col gap-3 p-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="text-[13px] font-semibold">Test Cases</span>
          <span className="text-[11px] bg-muted px-2 py-0.5 rounded-full text-muted-foreground">
            {testCases.length} total · {sampleCount} sample
          </span>
        </div>
        <Button
          size="sm"
          variant="outline"
          className="gap-1.5 text-[12px] h-8"
          onClick={addRow}
          disabled={saving === 'new'}
        >
          <Plus className="h-3.5 w-3.5" />
          Add Test Case
        </Button>
      </div>

      {testCases.length === 0 ? (
        <p className="text-[13px] text-muted-foreground py-8 text-center">
          No test cases yet. Click &quot;Add Test Case&quot; to add one.
        </p>
      ) : (
        <div className="border rounded-lg overflow-hidden">
          <div className="grid grid-cols-[32px_1fr_1fr_90px_80px] gap-2 px-3 py-2 bg-muted/50 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground border-b">
            <div>#</div>
            <div>Input</div>
            <div>Expected Output</div>
            <div>Visibility</div>
            <div />
          </div>

          {testCases.map((tc, i) => {
            const edit = getEdit(tc);
            const isDirty = edits[tc.id] !== undefined;
            return (
              <div
                key={tc.id}
                className={cn(
                  'grid grid-cols-[32px_1fr_1fr_90px_80px] gap-2 px-3 py-3 items-start border-b last:border-b-0',
                  isDirty && 'bg-amber-50/40',
                )}
              >
                <div className="text-[12px] text-muted-foreground pt-2">{i + 1}</div>

                <textarea
                  value={edit.input}
                  onChange={e => setField(tc.id, 'input', e.target.value)}
                  rows={3}
                  className="w-full border rounded-md px-2 py-1.5 text-[12px] font-mono bg-white resize-vertical focus:outline-none focus:ring-1 focus:ring-ring"
                />

                <textarea
                  value={edit.output}
                  onChange={e => setField(tc.id, 'output', e.target.value)}
                  rows={3}
                  className="w-full border rounded-md px-2 py-1.5 text-[12px] font-mono bg-white resize-vertical focus:outline-none focus:ring-1 focus:ring-ring"
                />

                <div className="flex flex-col gap-1.5 pt-1">
                  <button
                    onClick={() => setField(tc.id, 'is_sample', !edit.is_sample)}
                    className={cn(
                      'px-2 py-1 rounded-full text-[11px] font-semibold border transition-colors',
                      edit.is_sample
                        ? 'bg-emerald-50 border-emerald-500 text-emerald-600'
                        : 'bg-muted border-border text-muted-foreground',
                    )}
                  >
                    {edit.is_sample ? '✓ Sample' : 'Hidden'}
                  </button>
                  {isDirty && (
                    <button
                      onClick={() => saveRow(tc)}
                      disabled={saving === tc.id}
                      className={cn('px-2 py-1 rounded-full text-[11px] font-semibold', PRIMARY_ACTION)}
                    >
                      {saving === tc.id ? '…' : 'Save'}
                    </button>
                  )}
                </div>

                <div className="flex items-start pt-1">
                  <button
                    onClick={() => deleteRow(tc.id)}
                    disabled={saving === tc.id}
                    className="p-1.5 rounded-md text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
